import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../../hooks/useTheme';
import i18n from '../../../i18n';

const LOGO_URL = 'https://public.readdy.ai/ai/img_res/30ce4755-e8b9-4ea3-9fec-24f4c0149c4e.png';
const LOGIN_URL = 'https://ebm-app.onrender.com/auth/login';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [lang, setLang] = useState<'es' | 'en'>(() => {
    try { return (localStorage.getItem('innoflex-lang') || 'es') as 'es' | 'en'; } catch { return 'es'; }
  });

  const regularLinks = [
    { label: t('nav_services'),     href: '#services' },
    { label: t('nav_pricing'),      href: '#pricing' },
    { label: t('nav_testimonials'), href: '#testimonials' },
    { label: t('nav_contact'),      href: '#contact' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    if (location.pathname === '/') {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/' + href);
    }
  };

  const toggleLang = () => {
    const next = lang === 'es' ? 'en' : 'es';
    i18n.changeLanguage(next);
    setLang(next);
    try { localStorage.setItem('innoflex-lang', next); } catch { /* ignore */ }
    setTimeout(() => window.location.reload(), 80);
  };

  const isLight = theme === 'light';
  const showBg = scrolled || isLight;

  const transparentTextStyle  = (!scrolled && !isLight) ? { color: 'rgba(255,255,255,0.88)' } : undefined;
  const transparentSubStyle   = (!scrolled && !isLight) ? { color: 'rgba(255,255,255,0.45)' } : undefined;
  const transparentToggleStyle = (!scrolled && !isLight)
    ? { color: '#ffffff', borderColor: 'rgba(255,255,255,0.25)', backgroundColor: 'rgba(255,255,255,0.10)' }
    : undefined;

  const scrolledBg = isLight
    ? 'bg-white backdrop-blur-md border-b border-slate-200 shadow-sm'
    : 'bg-navy-900/95 backdrop-blur-md border-b border-navy-700';

  const navLinkClass   = isLight ? 'text-navy-800' : 'text-white/80';
  const logoTextClass  = isLight ? 'text-navy-950' : 'text-white';
  const logoSubClass   = isLight ? 'text-navy-900/50' : 'text-white/50';
  const menuToggleClass = isLight ? 'text-navy-950' : 'text-white';
  const toggleBtnClass  = isLight
    ? 'bg-slate-100 border-slate-300 text-navy-950 hover:bg-slate-200'
    : 'bg-white/10 border-white/20 text-white hover:bg-white/20';
  const loginBtnClass = isLight
    ? 'border-slate-300 text-navy-800 hover:bg-slate-100'
    : 'border-white/20 text-white/80 hover:bg-white/10';
  const mobileBg        = isLight ? 'bg-white border-slate-200' : 'bg-navy-900/98 border-navy-700';
  const mobileLinkClass = isLight ? 'text-navy-800' : 'text-white/80';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showBg ? scrolledBg : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          onClick={(e) => {
            if (location.pathname === '/') {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
          className="cursor-pointer flex items-center gap-3 flex-shrink-0"
        >
          <div className="rounded-xl overflow-hidden flex items-center justify-center">
            <img
              src={LOGO_URL}
              alt="InnoFlex Logo"
              className="h-14 w-auto object-contain transition-all duration-300"
              style={{ filter: !scrolled && !isLight ? 'brightness(1.4) drop-shadow(0 0 8px rgba(0,207,255,0.55))' : 'brightness(1.1)' }}
            />
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span
              style={transparentTextStyle}
              className={`font-heading text-2xl font-extrabold tracking-tight transition-colors duration-300 ${logoTextClass}`}
            >
              Inno<span className="text-electric">Flex</span>
            </span>
            <span
              style={transparentSubStyle}
              className={`font-body text-[10px] tracking-widest uppercase mt-1 transition-colors duration-300 ${logoSubClass}`}
            >
              {t('nav_tagline')}
            </span>
          </div>
        </Link>

        {/* Desktop Links */}
        <ul className="hidden xl:flex items-center gap-7 xl:mx-10">
          {/* Regular links */}
          {regularLinks.map((link) => (
            <li key={link.href}>
              <button
                style={transparentTextStyle}
                onClick={() => handleNavClick(link.href)}
                className={`text-sm font-medium hover:text-electric transition-colors duration-200 cursor-pointer whitespace-nowrap font-body ${navLinkClass}`}
              >
                {link.label}
              </button>
            </li>
          ))}

          {/* Industries → scrolls to "La Familia InnoFlex" section */}
          <li>
            <button
              style={transparentTextStyle}
              onClick={() => handleNavClick('#industries')}
              className={`flex items-center gap-1.5 text-sm font-medium hover:text-electric transition-colors duration-200 cursor-pointer whitespace-nowrap font-body ${navLinkClass}`}
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-building-2-line text-xs" />
              </div>
              {t('nav_industries')}
            </button>
          </li>

          {/* FAQ */}
          <li>
            <button
              style={transparentTextStyle}
              onClick={() => handleNavClick('#faq')}
              className={`text-sm font-medium hover:text-electric transition-colors duration-200 cursor-pointer whitespace-nowrap font-body ${navLinkClass}`}
            >
              {t('nav_faq')}
            </button>
          </li>
        </ul>

        {/* Controls */}
        <div className="hidden xl:flex items-center gap-2.5">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            title={isLight ? t('theme_dark') : t('theme_light')}
            style={transparentToggleStyle}
            className={`flex items-center justify-center w-8 h-8 rounded-full border transition-all duration-200 cursor-pointer ${toggleBtnClass}`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={isLight ? 'ri-moon-line text-sm' : 'ri-sun-line text-sm'}></i>
            </div>
          </button>

          {/* Language Toggle */}
          <button
            onClick={toggleLang}
            style={transparentToggleStyle}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full border text-xs font-bold font-body transition-all duration-200 cursor-pointer whitespace-nowrap ${toggleBtnClass}`}
            title="Switch language"
          >
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-global-line text-xs"></i>
            </div>
            {lang === 'es' ? 'ES' : 'EN'}
          </button>

          {/* Login */}
          <a
            href={LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`border px-3.5 py-1.5 rounded-full text-xs font-semibold font-body transition-all duration-200 cursor-pointer whitespace-nowrap ${loginBtnClass}`}
          >
            {t('nav_login')}
          </a>

          {/* Demo CTA */}
          <a
            href="#book-demo"
            className="relative flex items-center gap-2 bg-gradient-to-r from-electric to-emerald-400 hover:from-electric/90 hover:to-emerald-400/90 text-navy-950 text-sm font-extrabold px-6 py-2.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap font-body ring-2 ring-electric/30 hover:ring-electric/60 hover:scale-105 active:scale-95"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-schedule-line text-sm"></i>
            </div>
            {t('nav_demo')}
          </a>
        </div>

        {/* Mobile controls */}
        <div className="xl:hidden flex items-center gap-2">
          <button
            onClick={toggleLang}
            style={transparentToggleStyle}
            className={`flex items-center gap-1 px-2 py-1.5 rounded-full border text-xs font-bold font-body transition-all duration-200 cursor-pointer whitespace-nowrap ${toggleBtnClass}`}
          >
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-global-line text-xs"></i>
            </div>
            {lang === 'es' ? 'ES' : 'EN'}
          </button>

          <button
            onClick={toggleTheme}
            style={transparentToggleStyle}
            className={`flex items-center justify-center w-8 h-8 rounded-full border transition-all duration-200 cursor-pointer ${toggleBtnClass}`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={isLight ? 'ri-moon-line text-sm' : 'ri-sun-line text-sm'}></i>
            </div>
          </button>

          <button
            style={transparentTextStyle}
            className={`cursor-pointer transition-colors duration-300 ${menuToggleClass}`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className={`xl:hidden backdrop-blur-md border-t px-6 py-4 ${mobileBg}`}>
          <ul className="flex flex-col gap-1 mb-4">
            {regularLinks.map((link) => (
              <li key={link.href}>
                <button
                  onClick={() => handleNavClick(link.href)}
                  className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium hover:text-electric transition-colors duration-200 cursor-pointer font-body ${mobileLinkClass}`}
                >
                  {link.label}
                </button>
              </li>
            ))}

            {/* Industries link mobile */}
            <li>
              <button
                onClick={() => handleNavClick('#industries')}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium hover:text-electric transition-colors duration-200 cursor-pointer font-body ${mobileLinkClass}`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-building-2-line text-sm" />
                </div>
                {t('nav_industries')}
              </button>
            </li>

            {/* FAQ link mobile */}
            <li>
              <button
                onClick={() => handleNavClick('#faq')}
                className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium hover:text-electric transition-colors duration-200 cursor-pointer font-body ${mobileLinkClass}`}
              >
                {t('nav_faq')}
              </button>
            </li>
          </ul>

          <div className="flex flex-col gap-2.5 mt-4">
            <a
              href={LOGIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`text-center border px-4 py-2.5 rounded-full text-sm font-semibold font-body transition-all duration-200 cursor-pointer whitespace-nowrap ${loginBtnClass}`}
            >
              {t('nav_login')}
            </a>
            <a
              href="#book-demo"
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-electric to-emerald-400 text-navy-950 text-sm font-extrabold px-5 py-3 rounded-full cursor-pointer whitespace-nowrap font-body w-full ring-2 ring-electric/30"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-schedule-line text-sm"></i>
              </div>
              {t('nav_demo')}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
